import {
  AimOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FolderOutlined,
  FormOutlined,
  MessageOutlined,
  ProjectOutlined,
  SafetyOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
  TruckOutlined,
  UsergroupAddOutlined
} from "@ant-design/icons"
import { Select } from "antd"
import React, { useId } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { cn } from "../../../../utils/cn.ts"
import type { SearchResult, SectionLayout } from "../../helpers/layout.ts"

// The same glyphs the app's own sidebar gives these modules.
const SECTION_ICONS: Record<string, React.ReactNode> = {
  projects: <ProjectOutlined aria-hidden />,
  leads: <AimOutlined aria-hidden />,
  calendar: <CalendarOutlined aria-hidden />,
  files: <FolderOutlined aria-hidden />,
  locations: <EnvironmentOutlined aria-hidden />,
  customers: <TeamOutlined aria-hidden />,
  subcontractors: <SafetyOutlined aria-hidden />,
  suppliers: <TruckOutlined aria-hidden />,
  esign: <FormOutlined aria-hidden />,
  communication: <MessageOutlined aria-hidden />,
  team_management: <UsergroupAddOutlined aria-hidden />,
  settings: <SettingOutlined aria-hidden />
}

const sectionIcon = (key: string) =>
  SECTION_ICONS[key] ?? <AppstoreOutlined aria-hidden />

interface PermissionsSectionNavProps {
  layouts: SectionLayout[]
  activeKey: string | null
  query: string
  results: SearchResult[]
  onQueryChange: (query: string) => void
  onSelectSection: (key: string) => void
  onSelectResult: (result: SearchResult) => void
}

export function PermissionsSectionNav({
  layouts,
  activeKey,
  query,
  results,
  onQueryChange,
  onSelectSection,
  onSelectResult
}: PermissionsSectionNavProps) {
  const { t } = useTranslation()
  const resultsId = useId()
  const searching = query.trim().length > 0

  return (
    <aside className="flex min-h-0 flex-col gap-3 border-b border-border bg-white p-3 lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r lg:p-4">
      {/* Finding and switching sections is navigation, so a read-only role
          (the GCC Owner preview disables the whole form) still allows it. */}
      <TextInput
        disabled={false}
        disableAutoCapitalize
        allowClear
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        prefix={<SearchOutlined aria-hidden className="text-gray-400" />}
        placeholder={t("permissions.editor.findPlaceholder")}
        aria-label={t("permissions.editor.findLabel")}
        aria-controls={searching ? resultsId : undefined}
      />

      {searching ? (
        <div
          id={resultsId}
          className="min-h-0 max-h-72 overflow-auto tailwind-scroll lg:max-h-none lg:flex-1"
        >
          <p
            className="m-0 mb-1 px-1 text-xs text-gray-500"
            role="status"
            aria-live="polite"
          >
            {t("permissions.editor.resultsCount", { count: results.length })}
          </p>
          {results.length === 0 ? (
            <div className="rounded-md bg-muted px-3 py-4 text-center">
              <p className="m-0 text-sm font-medium text-gray-800">
                {t("permissions.editor.noResults", { query: query.trim() })}
              </p>
              <p className="m-0 mt-1 text-xs text-gray-500">
                {t("permissions.editor.noResultsHint")}
              </p>
            </div>
          ) : (
            <ul className="m-0 list-none p-0">
              {results.map((result) => (
                <li key={result.actionKey}>
                  <button
                    type="button"
                    onClick={() => onSelectResult(result)}
                    className="flex w-full flex-col items-start rounded-md px-2.5 py-2 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <span className="text-sm text-gray-900">
                      {result.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {result.sectionTitle}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <Select
            disabled={false}
            className="w-full lg:hidden"
            value={activeKey ?? undefined}
            onChange={onSelectSection}
            aria-label={t("permissions.editor.sectionSelectLabel")}
            options={layouts.map((layout) => ({
              value: layout.key,
              label: (
                <span className="inline-flex items-center gap-2">
                  {sectionIcon(layout.key)}
                  {layout.title}
                </span>
              )
            }))}
          />
          <nav
            aria-label={t("permissions.editor.sectionsHeading")}
            className="hidden min-h-0 flex-1 overflow-auto tailwind-scroll lg:block"
          >
            <p className="m-0 mb-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t("permissions.editor.sectionsHeading")}
            </p>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {layouts.map((layout) => {
                const active = layout.key === activeKey
                return (
                  <li key={layout.key}>
                    <button
                      type="button"
                      aria-current={active ? "page" : undefined}
                      onClick={() => onSelectSection(layout.key)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2 text-left text-sm text-gray-700 hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary",
                        active &&
                          "border-primary bg-primary-bg font-medium text-primary-strong hover:bg-primary-bg"
                      )}
                    >
                      <span
                        className={cn(
                          "text-base",
                          active ? "text-primary" : "text-gray-500"
                        )}
                      >
                        {sectionIcon(layout.key)}
                      </span>
                      {layout.title}
                    </button>
                  </li>
                )
              })}
            </ul>
          </nav>
        </>
      )}
    </aside>
  )
}
